import { resolveTableCellLinkHref } from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

const STORE_PATH_PATTERN =
  /(^|[^\w/.:%-])(\/(?:products|collections|pages)\/[\w%-]+)/g;

const EXACT_STORE_PATH_PATTERN = /^\/(?:products|collections|pages)\/[\w%-]+$/;

/**
 * Returns the store path when inline code content is exactly one
 * `/products/{handle}` or `/collections/{handle}` path.
 */
export function getInlineStorePath(children: unknown): string | null {
  const text = Array.isArray(children) ? children.join(``) : children;
  if (typeof text !== `string`) return null;
  const trimmed = text.trim();
  return EXACT_STORE_PATH_PATTERN.test(trimmed) ? trimmed : null;
}

interface MarkdownNode {
  type: string;
  value?: string;
  url?: string;
  children?: MarkdownNode[];
}

function createLinkNode(path: string, siteDomain: string): MarkdownNode {
  return {
    type: `link`,
    url: resolveTableCellLinkHref(path, siteDomain),
    children: [{ type: `text`, value: path }],
  };
}

function splitTextByStorePaths(
  text: string,
  siteDomain: string,
): MarkdownNode[] {
  const nodes: MarkdownNode[] = [];
  let lastIndex = 0;

  Array.from(text.matchAll(STORE_PATH_PATTERN)).forEach((match) => {
    const [, prefix, path] = match;
    const start = (match.index ?? 0) + prefix.length;
    if (start > lastIndex) {
      nodes.push({ type: `text`, value: text.slice(lastIndex, start) });
    }
    nodes.push(createLinkNode(path, siteDomain));
    lastIndex = start + path.length;
  });

  if (lastIndex < text.length) {
    nodes.push({ type: `text`, value: text.slice(lastIndex) });
  }
  return nodes;
}

/**
 * Turns plain `/products/{handle}` and `/collections/{handle}` mentions in
 * markdown text into links on the account's store domain.
 */
export function remarkStorePaths(siteDomain?: string) {
  return (tree: MarkdownNode) => {
    if (!siteDomain) return;

    const visit = (node: MarkdownNode) => {
      if (!node.children) return;
      const nextChildren: MarkdownNode[] = [];
      node.children.forEach((child) => {
        if (child.type === `text` && child.value) {
          nextChildren.push(...splitTextByStorePaths(child.value, siteDomain));
          return;
        }
        if (child.type !== `link` && child.type !== `linkReference`) {
          visit(child);
        }
        nextChildren.push(child);
      });
      node.children = nextChildren;
    };

    visit(tree);
  };
}
